import axios from 'axios'

import type { Rundown } from '~shared/rundown/rundown'

import { rundownApi } from '../../../axios'
import { apiErrorHandler } from '../../../utils/api-error-handler.ts'
import type { RequestUploadDto, ConfirmUploadDto } from '../model/dto.ts'
import type {
  GroupRundownCuesParams,
  ReorderRundownCuesParams,
  UploadRundownFileParams,
  AddRundownCueParams,
  UpdateRundownCueParams,
  DuplicateRundownCueParams,
  DeleteRundownCueParams,
  UpdateRundownParams,
  CreateRundownRunnerParams,
  DuplicateRundownCuesParams,
  RequestUploadParams,
  ConfirmUploadParams,
  UngroupRundownCuesParams,
  GetRundownsByTeamParams,
  CloneRundownParams,
} from '../model/params.ts'

import type { AxiosRequestConfig } from 'axios'

export const updateRundown = async ({
  rundownId,
  payload,
}: UpdateRundownParams) => {
  return await apiErrorHandler(rundownApi.patch(`/${rundownId}`, payload))
}

/**
 * Request a signed URL for direct upload to Cloud Storage
 */
export const requestUploadUrl = async ({
  rundownId,
  payload,
  signal,
}: RequestUploadParams) => {
  return await apiErrorHandler(
    rundownApi.post<RequestUploadDto>(`/${rundownId}/request-upload`, payload, {
      signal,
    }),
  )
}

/**
 * Upload file directly to Cloud Storage using signed URL
 */
export const uploadToSignedUrl = async (
  uploadUrl: string,
  file: File,
  contentType: string,
  maxFileSize: number,
  signal: AbortSignal,
  onProgress?: (progress: number) => void,
) => {
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': contentType,
      'x-goog-content-length-range': `0,${maxFileSize}`,
    },
    onUploadProgress: (event) => {
      if (event.total) {
        onProgress?.(Math.round((event.loaded / event.total) * 100))
      }
    },
    signal,
  }

  // Use raw axios for direct Cloud Storage upload (no auth interceptors)
  return await axios.put(uploadUrl, file, config)
}

/**
 * Confirm that the file was successfully uploaded
 */
export const confirmUploadFile = async ({
  rundownId,
  payload,
}: ConfirmUploadParams) => {
  return await apiErrorHandler(
    rundownApi.post<ConfirmUploadDto>(`/${rundownId}/confirm-upload`, payload),
  )
}

/**
 * Upload a file to Cloud Storage using the new signed URL workflow
 * This is the preferred method for all file uploads, especially files >5MB
 */
export const uploadRundownFile = async ({
  rundownId,
  payload,
  signal,
  onProgress,
}: UploadRundownFileParams) => {
  // Step 1: Request signed upload URL (5% progress)
  onProgress?.(5)
  const contentType = payload.file.type || 'application/octet-stream'
  const { data: uploadData } = await requestUploadUrl({
    rundownId,
    payload: {
      filename: payload.file.name,
      contentType,
      fileSize: payload.file.size,
    },
    signal,
  })

  signal?.throwIfAborted()

  // Step 2: Upload directly to Cloud Storage (5-95% progress)
  await uploadToSignedUrl(
    uploadData.uploadUrl,
    payload.file,
    contentType,
    uploadData.maxFileSize,
    signal,
    (uploadProgress) => {
      onProgress?.(5 + Math.round(uploadProgress * 0.9))
    },
  )

  signal?.throwIfAborted()

  // Step 3: Confirm upload (95-100% progress)
  onProgress?.(95)
  const result = await confirmUploadFile({
    rundownId,
    payload: {
      filePath: uploadData.filePath,
    },
  })

  onProgress?.(100)
  return result
}

export const addRundownCue = async ({
  rundownId,
  payload,
  params,
}: AddRundownCueParams) => {
  return await apiErrorHandler(
    rundownApi.post(`/${rundownId}/cues`, payload, { params }),
  )
}

export const updateRundownCue = async ({
  rundownId,
  cueId,
  payload,
  params,
}: UpdateRundownCueParams) => {
  return await apiErrorHandler(
    rundownApi.patch(`/${rundownId}/cues/${cueId}`, payload, { params }),
  )
}

export const duplicateRundownCue = async ({
  rundownId,
  cueId,
}: DuplicateRundownCueParams) => {
  return await apiErrorHandler(
    rundownApi.post(`/${rundownId}/cues/${cueId}/clone`),
  )
}

export const duplicateRundownCues = async ({
  rundownId,
  payload,
}: DuplicateRundownCuesParams) => {
  return await apiErrorHandler(
    rundownApi.post(`/${rundownId}/cues/clone`, payload),
  )
}

export const deleteRundownCue = async ({
  rundownId,
  cueId,
}: DeleteRundownCueParams) => {
  return await apiErrorHandler(rundownApi.delete(`/${rundownId}/cues/${cueId}`))
}

export const createRundownRunner = async ({
  rundownId,
}: CreateRundownRunnerParams) => {
  return await apiErrorHandler(rundownApi.post(`/${rundownId}/create-runner`))
}

export const reorderRundownCues = ({
  rundownId,
  cues,
}: ReorderRundownCuesParams) => {
  return apiErrorHandler(rundownApi.patch(`/${rundownId}/reorderCues`, cues))
}

export const groupRundownCues = ({
  rundownId,
  beforeCueId,
  cuesToGroupIds,
}: GroupRundownCuesParams) => {
  return apiErrorHandler(
    rundownApi.patch(`/${rundownId}/groupCues`, {
      beforeCueId,
      cuesToGroupIds,
    }),
  )
}

export const ungroupRundownCues = ({
  rundownId,
  payload,
}: UngroupRundownCuesParams) => {
  return apiErrorHandler(
    rundownApi.patch<Rundown>(`/${rundownId}/ungroupCues`, payload),
  )
}

export const getRundownsByTeam = async (
  { teamId }: GetRundownsByTeamParams,
  { toast }: { toast?: boolean } = {},
) => {
  return await apiErrorHandler(
    rundownApi.get<Rundown[]>(`/get-by-team/${teamId}`),
    { toast: toast ?? true },
  )
}

export const getTemplatesByTeam = async ({
  teamId,
}: GetRundownsByTeamParams) => {
  return await apiErrorHandler(
    rundownApi.get<Rundown[]>(`/get-templates-by-team/${teamId}`),
  )
}

export const cloneRundown = async ({
  rundownId,
  teamId,
  name,
}: CloneRundownParams) => {
  return await apiErrorHandler(
    rundownApi.post<Rundown>(`/${rundownId}/clone`, { teamId, name }),
  )
}
